import { capturePostHogEvent } from "@/lib/analytics/posthog-server";
import { hashIp } from "./ip-hash";

type SecurityDetails = Record<string, unknown>;

// Always forwarded to PostHog — low-volume, high-signal events.
// Includes ban/block events: each one is a discrete incident we want to see
// every time, not just 1-in-10. Only rate_limit_exceeded stays sampled
// because it can fire thousands of times per minute under an attack.
const POSTHOG_SECURITY_EVENTS = new Set([
	"challenge_redirected",
	"challenge_verified",
	"false_positive_candidate",
	// Synthesised here when an IP crosses the attack threshold below.
	"scraping_attack_detected",
	// Elevated to always-sent (previously sampled): these are low-volume by
	// nature and each one is a real incident worth seeing every time.
	"soft_ban_triggered",
	"ua_blocked",
	"sitemap_tripwire",
	"daily_cap_enforced",
	// Turnstile soft-fail escape hatch (flag default OFF) — every acceptance
	// is a deliberate bot-check bypass and must be queryable for audit.
	"turnstile_softfail_accepted",
	// Fires on EVERY real Resend webhook call while `RESEND_WEBHOOK_SECRET` is
	// unset in production — a misconfiguration, not routine traffic. Always-sent
	// (not sampled) so it can't go unnoticed again the way it did for ~7 days in
	// 2026-07 (console.warn-only, nobody watching). See the header comment in
	// `src/app/api/webhook/email/route.ts`.
	"resend_webhook_secret_missing",
]);

// Sampled to PostHog at SAMPLE_RATE — rate_limit_exceeded alone can fire
// thousands of times per minute under an attack; a 1-in-10 sample is enough
// to see trends without blowing the ingest quota.
// `go_link_non_person`: a browser-UA hop to `/<locale>/go/<slug>` that no person
// clicked (headless browser, link scanner). Sampled because it ran at ~1,300/day
// before the 2026-10-07 fix — the count measures that the partner no longer sees them.
const POSTHOG_SECURITY_EVENTS_SAMPLED = new Set([
	"rate_limit_exceeded",
	"go_link_non_person",
]);

const SAMPLE_RATE = 0.1; // 1-in-10 for sampled events

// Per-IP attack detection. When ATTACK_THRESHOLD trigger events arrive from
// one IP inside ATTACK_WINDOW_MS, fan out a Telegram alert. The alert is
// silenced for ATTACK_COOLDOWN_MS so a sustained attack pings the founder
// once per cooldown rather than thousands of times.
//
// The counting and the cooldown live at DIFFERENT scopes, deliberately:
//
//   - counting  -> isolate memory. Cheap, on the request path, and an
//                  approximation is fine: it only decides when to ask.
//   - cooldown  -> D1 (`claimAttackAlertSlot`). This is the part that has to
//                  be a real bound, and isolate memory cannot be one. Workers
//                  runs many isolates concurrently and each keeps its own
//                  `lastAlertAt`, so one scraper pages once per isolate per
//                  cooldown. Measured 2026-09-01: the same IP hash sent 5
//                  alerts in 1 minute (06:47-06:48) and 6 in 2 minutes
//                  (09:14-09:16) — 11 of that day's ~80 Telegram messages,
//                  all restating one scraper.
const ATTACK_TRIGGER_EVENTS = new Set([
	"rate_limit_exceeded",
	"soft_ban_triggered",
	"ua_blocked",
	"sitemap_tripwire",
	"daily_cap_enforced",
]);
const ATTACK_THRESHOLD = 50;
const ATTACK_WINDOW_MS = 5 * 60_000;
const ATTACK_COOLDOWN_MS = 30 * 60_000;
const ATTACK_STATE_MAX_IPS = 1024;

interface AttackState {
	events: Map<string, number[]>;
	lastAlertAt: Map<string, number>;
}

function getAttackState(): AttackState {
	const globalAny = globalThis as unknown as {
		__ldSecurityAttack?: AttackState;
	};
	if (!globalAny.__ldSecurityAttack) {
		globalAny.__ldSecurityAttack = {
			events: new Map(),
			lastAlertAt: new Map(),
		};
	}
	return globalAny.__ldSecurityAttack;
}

export function __resetAttackState() {
	const globalAny = globalThis as unknown as {
		__ldSecurityAttack?: AttackState;
	};
	globalAny.__ldSecurityAttack = undefined;
}

function purgeAttackState(state: AttackState, now: number) {
	if (state.events.size <= ATTACK_STATE_MAX_IPS) return;
	for (const [ip, timestamps] of state.events) {
		const fresh = timestamps.filter((t) => now - t < ATTACK_WINDOW_MS);
		if (fresh.length === 0) state.events.delete(ip);
		else state.events.set(ip, fresh);
	}
	for (const [ip, ts] of state.lastAlertAt) {
		if (now - ts > ATTACK_COOLDOWN_MS) state.lastAlertAt.delete(ip);
	}
}

function registerEventAndDetectAttack(
	ip: string,
	now: number,
): { breached: boolean; count: number } {
	const state = getAttackState();
	const existing = state.events.get(ip) ?? [];
	const recent = existing.filter((t) => now - t < ATTACK_WINDOW_MS);
	recent.push(now);
	state.events.set(ip, recent);
	purgeAttackState(state, now);

	if (recent.length < ATTACK_THRESHOLD) {
		return { breached: false, count: recent.length };
	}
	// Per-isolate pre-filter only. `claimAttackAlertSlot` is what actually
	// bounds the alert rate; this exists so the common case never touches D1.
	const lastAlert = state.lastAlertAt.get(ip) ?? 0;
	if (now - lastAlert < ATTACK_COOLDOWN_MS) {
		return { breached: false, count: recent.length };
	}
	state.lastAlertAt.set(ip, now);
	return { breached: true, count: recent.length };
}

/**
 * Claims the alert slot for one IP hash fleet-wide.
 *
 * The UPSERT *is* the claim: SQLite applies `DO UPDATE ... WHERE` atomically,
 * so exactly one isolate sees `changes === 1` and every other isolate inside
 * the cooldown sees 0. Only ever reached once the local pre-filter above has
 * already fired, so this is not a per-request D1 hit even under a scrape.
 *
 * Keyed on the PSEUDONYMIZED hash, never the raw address. Fails OPEN — a D1
 * error must not silence a real attack alert, and the per-isolate cooldown
 * still bounds the volume if D1 is unreachable.
 */
const ATTACK_CLAIM_SQL = `INSERT INTO system_config (key, value, updated_at, updated_by)
VALUES (?1, ?2, unixepoch(), 'security')
ON CONFLICT(key) DO UPDATE SET value = excluded.value, updated_at = excluded.updated_at, updated_by = excluded.updated_by
WHERE system_config.updated_at <= unixepoch() - ?3`;

export async function claimAttackAlertSlot(
	ipHash: string,
	env: Partial<CloudflareEnv>,
	now: number,
): Promise<boolean> {
	const db = env.DB;
	if (!db) return true;
	try {
		const res = await db
			.prepare(ATTACK_CLAIM_SQL)
			.bind(
				`security_attack_alert:${ipHash}`,
				String(now),
				Math.round(ATTACK_COOLDOWN_MS / 1000),
			)
			.run();
		return (res.meta?.changes ?? 0) > 0;
	} catch {
		return true;
	}
}

async function sha1Hex(input: string): Promise<string> {
	const data = new TextEncoder().encode(input);
	const digest = await crypto.subtle.digest("SHA-1", data);
	return Array.from(new Uint8Array(digest))
		.map((b) => b.toString(16).padStart(2, "0"))
		.join("");
}

export async function fingerprintUa(ua: string | null | undefined) {
	if (!ua) return "none";
	const head = ua.slice(0, 32);
	const hash = (await sha1Hex(ua)).slice(0, 8);
	return `${head}#${hash}`;
}

function escapeTelegramHtml(value: string): string {
	return value
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;");
}

function truncate(value: string, max = 80): string {
	if (value.length <= max) return value;
	return `${value.slice(0, max - 1)}…`;
}

/**
 * Pseudonymizes an IP for use as a PostHog `distinctId` / attack-alert label.
 * `"unknown"` (no IP resolved) is passed through unhashed — it is a sentinel,
 * not a real address, so hashing it would only obscure a legitimate "no IP"
 * signal without protecting anything.
 */
async function hashIpForTelemetry(
	ip: string,
	env: Partial<CloudflareEnv>,
): Promise<string> {
	if (ip === "unknown") return "unknown";
	return hashIp(ip, env.IP_HASH_PEPPER);
}

/**
 * Formats the Telegram attack alert. `input.ip` is expected to already be a
 * pseudonymized hash (or `"unknown"`) — callers must hash the raw IP before
 * calling this, never pass a real address through to a third-party chat log.
 */
export function formatAttackAlertMessage(input: {
	trigger: string;
	ip: string;
	count: number;
	windowMinutes: number;
	details: SecurityDetails;
}): string {
	const lines = [
		"🚨 <b>Possible scraping attack</b>",
		`Trigger: <code>${escapeTelegramHtml(input.trigger)}</code>`,
		`IP hash: <code>${escapeTelegramHtml(input.ip)}</code>`,
		`Events: ${input.count} in ${input.windowMinutes} min`,
	];
	const botClass = input.details.botClass;
	if (typeof botClass === "string" && botClass.length > 0) {
		lines.push(`Class: ${escapeTelegramHtml(botClass)}`);
	}
	const route = input.details.route;
	if (typeof route === "string" && route.length > 0) {
		lines.push(
			`Route: <code>${escapeTelegramHtml(truncate(route, 80))}</code>`,
		);
	}
	const ua = input.details.ua;
	if (typeof ua === "string" && ua.length > 0) {
		lines.push(`UA: ${escapeTelegramHtml(truncate(ua, 80))}`);
	}
	return lines.join("\n");
}

async function fireAttackAlert(
	trigger: string,
	ip: string,
	count: number,
	details: SecurityDetails,
	env: Partial<CloudflareEnv>,
) {
	// Pseudonymize before the IP touches a third-party surface (Telegram) or a
	// long-lived analytics store (PostHog) — neither should ever see the raw
	// address, only a short deterministic hash.
	const ipHash = await hashIpForTelemetry(ip, env);

	// Fleet-wide bound. The per-isolate cooldown that got us here is a
	// pre-filter, not a limit — see the note on ATTACK_COOLDOWN_MS.
	if (!(await claimAttackAlertSlot(ipHash, env, Date.now()))) return;

	const message = formatAttackAlertMessage({
		trigger,
		ip: ipHash,
		count,
		windowMinutes: Math.round(ATTACK_WINDOW_MS / 60_000),
		details,
	});

	const tasks: Array<Promise<unknown>> = [];

	const { sendTelegramAlert } = await import("@/lib/telegram/send-alert");
	tasks.push(sendTelegramAlert(message).catch(() => undefined));

	// Mirror the alert into PostHog as a high-signal event so it shows up in
	// dashboards alongside the sampled raw events.
	tasks.push(
		capturePostHogEvent(env, {
			distinctId: `ip:${ipHash}`,
			event: "security_scraping_attack_detected",
			properties: {
				trigger,
				ipHash,
				event_count: count,
				window_ms: ATTACK_WINDOW_MS,
				threshold: ATTACK_THRESHOLD,
				botClass: details.botClass,
				route: details.route,
				ua: details.ua,
			},
		}).catch(() => undefined),
	);

	await Promise.allSettled(tasks);
}

export function logSecurityEvent(
	event: string,
	details: SecurityDetails = {},
	env?: Partial<CloudflareEnv>,
	/**
	 * Workers ExecutionContext.waitUntil — when provided, async side-effects
	 * (PostHog capture, Telegram alert) are registered with it so they survive
	 * the response being sent. Without it, they fire-and-forget (safe but may
	 * be dropped on a short-lived isolate).
	 */
	waitUntil?: (p: Promise<unknown>) => void,
) {
	console.warn("[security]", event, details);

	if (!env) return;

	const ip = (details.ip as string | undefined) ?? "unknown";

	// Helper: register a promise with waitUntil when available, otherwise
	// fire-and-forget (void) so non-CF callers and tests are unaffected.
	const track = (p: Promise<unknown>) => {
		if (waitUntil) {
			waitUntil(p);
		} else {
			void p;
		}
	};

	// details.ip (when present) is the raw client address — never let it reach
	// PostHog. Scrub it from properties and use only the pseudonymized hash for
	// the distinctId; every other field (route, botClass, ua, etc.) passes
	// through untouched.
	const { ip: _rawIp, ...detailsWithoutIp } = details;

	if (POSTHOG_SECURITY_EVENTS.has(event)) {
		track(
			(async () => {
				const ipHash = await hashIpForTelemetry(ip, env);
				return capturePostHogEvent(env, {
					distinctId: `ip:${ipHash}`,
					event: `security_${event}`,
					properties: { ...detailsWithoutIp, ipHash },
				});
			})().catch(() => {}),
		);
	} else if (POSTHOG_SECURITY_EVENTS_SAMPLED.has(event)) {
		if (Math.random() < SAMPLE_RATE) {
			track(
				(async () => {
					const ipHash = await hashIpForTelemetry(ip, env);
					return capturePostHogEvent(env, {
						distinctId: `ip:${ipHash}`,
						event: `security_${event}`,
						properties: {
							...detailsWithoutIp,
							ipHash,
							sampled: true,
							sample_rate: SAMPLE_RATE,
						},
					});
				})().catch(() => {}),
			);
		}
	}

	if (ATTACK_TRIGGER_EVENTS.has(event) && ip !== "unknown") {
		const { breached, count } = registerEventAndDetectAttack(ip, Date.now());
		if (breached) {
			track(fireAttackAlert(event, ip, count, details, env).catch(() => {}));
		}
	}
}
